import { UAParser } from 'ua-parser-js'

import { DeviceSettings, User, UserSdk } from './sdks/user'
import { canUseDom, canUseNavigator } from './utils/canUse'
import type { ABTastySdk } from './abtasty'
import { Configuration, type MetadataContent, type TrackingRequest, type TrackingResponseSuccess } from './generated'
import { initCoreSdk } from './initCoreSdk'
import { type StorageSdk } from './storage'
import {
  ClickEventId,
  getMarketLanguage,
  getTrackingDeviceType,
  getTrackingOs,
  PageName,
  PushEvent,
  SearchUsage,
  type TrackingSdk,
  type UniversalSearchType,
} from './tracking'

export * from './generated/model'
export * from './generated/configuration'
export * from './interceptors'
export * from './error'
export * from './sdks/user'
export * from './storage'
export * from './tracking'
export * from './userConstants'

export type MiniSdk = {
  trackingSdk: TrackingSdk
  storageSdk: StorageSdk
  trackingEvent?: {
    ClickEventId: {
      rechercheAutoCompletion: string
      rechercheLibre: string
      botAide: string
      rechercheExistante: string
      derniereRechercheSimple: string
      derniereRechercheComplete: string
      connexionCCLHeader: string
      connexionCCLNavBar: string
      creationCCLNavBar: string
      accederCCLNavBar: string
      recherchePopulaire: string
      languageSwitcher: string
    }
    PushEvent: {
      rechercheExistante: string
    }
    UniversalSearchType: { [key in UniversalSearchType]: key }
    SearchUsageEvent: {
      rechercheLibre: string
      rechercheAutoCompletion: string
      rechercheAutoCompletionSuggestion: string
      rechercheAutoCompletionHistorique: string
      rechercheHistoriqueRecherche: string
      recherchePopulaire: string
    }
    PageName: {
      Homepage: string
    }
  }
}

declare let ENV: string

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Vendor {
    id: string
    enabled: boolean
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    ivtsSdk: MiniSdk
    ivtsTag: {
      config: {
        clientId: string
        market: string
        bff: string | undefined
      }
      data: TrackingRequest
    }
    utag_data: Record<string, string | string[]> | undefined
    utag: {
      view: (datalayer: MetadataContent | undefined) => void
    }
    Didomi: {
      getUserConsentStatusForVendor(vendor: string): boolean
      getCurrentUserStatus(): {
        vendors: Record<string, Vendor>
        consent_string: string
      }
    }
    didomiEventListeners: {
      event: string
      listener: () => void
    }[]
    didomiOnReady: (() => void)[]
    ABTasty: ABTastySdk
  }
}

export const initLibSdk = (configuration: Configuration, userSdk: UserSdk): MiniSdk => {
  const {
    coreSdk: { trackingSdk },
  } = initCoreSdk({ configuration, userSdk, shouldIgnoreAuthInterceptor: true })

  return {
    trackingSdk,
    storageSdk: userSdk.storageSdk,
    trackingEvent: {
      ClickEventId: {
        rechercheAutoCompletion: ClickEventId.RechercheAutoCompletion.toString(),
        rechercheLibre: ClickEventId.RechercheLibre.toString(),
        botAide: ClickEventId.BotAide.toString(),
        rechercheExistante: ClickEventId.RechercheExistante.toString(),
        derniereRechercheSimple: ClickEventId.DerniereRechercheSimple.toString(),
        derniereRechercheComplete: ClickEventId.DerniereRechercheComplete.toString(),
        connexionCCLHeader: ClickEventId.ConnexionCCLHeader.toString(),
        connexionCCLNavBar: ClickEventId.ConnexionCCLNavBar.toString(),
        creationCCLNavBar: ClickEventId.CreationCCL_NavBar.toString(),
        accederCCLNavBar: ClickEventId.AccederCCL_NavBar.toString(),
        recherchePopulaire: ClickEventId.RecherchePopulaire.toString(),
        languageSwitcher: ClickEventId.LanguageSwitcher.toString(),
      },
      PushEvent: {
        rechercheExistante: PushEvent.RechercheExistante.toString(),
      },
      UniversalSearchType: {
        suggestions: 'suggestions',
        commonSearches: 'commonSearches',
        citiesAndStations: 'citiesAndStations',
        addresses: 'addresses',
        pois: 'pois',
        trends: 'trends',
        recentSearches: 'recentSearches',
        popularSearches: 'popularSearches',
      },
      SearchUsageEvent: {
        rechercheLibre: SearchUsage.LIBRE.toString(),
        rechercheAutoCompletion: SearchUsage.AUTOCOMPLETION.toString(),
        rechercheAutoCompletionSuggestion: SearchUsage.SUGGESTION.toString(),
        rechercheAutoCompletionHistorique: SearchUsage.HISTORIQUE.toString(),
        rechercheHistoriqueRecherche: SearchUsage.HISTORIQUE_RECHERCHE.toString(),
        recherchePopulaire: SearchUsage.POPULAIRE.toString(),
      },
      PageName: {
        Homepage: PageName.Homepage.toString(),
      },
    },
  }
}

export const getBffBasePath = (): string => {
  const relativePath = '/bff'

  switch (window.ivtsTag?.config?.bff) {
    case undefined:
      return relativePath
    case 'production':
      return `https://www.sncf-connect.com${relativePath}`
    default:
      return `${window.ivtsTag?.config?.bff}${relativePath}`
  }
}

export const initBffSdkLibrary = async (): Promise<MiniSdk> => {
  const param = { basePath: getBffBasePath() }
  const configuration = new Configuration(param)
  let osVersion = ''
  let deviceType = ''

  if (canUseNavigator()) {
    const uaParser = new UAParser(window.navigator.userAgent)
    const os = uaParser.getOS()
    osVersion = getTrackingOs(os.name, os.version)
    deviceType = getTrackingDeviceType(uaParser.getDevice().type)
  }
  const userSdk = new UserSdk(
    new User(
      new DeviceSettings(
        window.ivtsTag?.config?.market || getMarketLanguage(),
        'web',
        window.ivtsTag?.config?.clientId,
        ENV,
        osVersion,
        deviceType,
      ),
    ),
  )

  return initLibSdk(configuration, userSdk)
}

export const initDidomi = (trackingSdk: TrackingSdk): void => {
  window.didomiEventListeners = window.didomiEventListeners || []
  window.didomiEventListeners.push({
    event: 'consent.changed',
    async listener() {
      const consentString = window.Didomi?.getCurrentUserStatus().consent_string
      const vendors = window.Didomi?.getCurrentUserStatus().vendors || {}
      const vendorsWithPurposes = Object.entries(vendors)
        .filter(([, value]) => value.enabled)
        .map(([key]) => key)
      await trackingSdk.updateConsentData(vendorsWithPurposes, consentString)
    },
  })
  window.didomiOnReady = window.didomiOnReady || []
  window.didomiOnReady.push(async function updateMissingConsentData() {
    const vendors = window.Didomi?.getCurrentUserStatus().vendors || {}
    const vendorsWithPurposes = Object.entries(vendors)
      .filter(([, value]) => value.enabled)
      .map(([key]) => key)
    await trackingSdk.updateMissingConsentData(vendorsWithPurposes)
  })
}

initBffSdkLibrary().then((sdk) => {
  if (canUseDom()) {
    window.ivtsSdk = sdk // expose sdk for external partners
    sdk.trackingSdk.init()
    initDidomi(sdk.trackingSdk)
    document.addEventListener('click', (evt) => sdk.trackingSdk.updateClickPosition(evt), true)
    sdk.trackingSdk.trackExternalPage(window.ivtsTag?.data).then((trackingInfo: TrackingResponseSuccess): void => {
      sdk.trackingSdk.updatePreviousPageName(trackingInfo.datalayer?.stringValues?.page_name)

      let timeout = 10

      const poll = () => {
        setTimeout(() => {
          timeout--

          if (window.utag && window.utag.view) {
            window.utag_data = trackingInfo.datalayer?.stringValues || {}
            window.utag.view(window.utag_data)
          } else if (timeout > 0) {
            poll()
          } else {
            // eslint-disable-next-line no-console
            console.error('Tealium is not loaded')
          }
        }, 500)
      }
      poll()
    })
  }
})
